import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useCanonicalEventDetail } from '../hooks/useCanonicalEventDetail';
import { securityApi } from '../services/securityApi';
import type { SourceObservation } from '../types/security';
import { OperatorStateBadge } from './OperatorStateBadge';
import { SeverityBadge } from './SeverityBadge';
import { StatusBadge } from './StatusBadge';
import { ErrorState, LoadingSkeleton } from './LoadingSkeleton';
import { isRoutingEligible, severityTokenFromScore } from './ThreatsTab';
import { SnoozePicker } from './SnoozePicker';

function EvidenceTrail({
  observations,
}: {
  observations: SourceObservation[];
}): React.JSX.Element {
  return (
    <div>
      <h4>Evidence trail ({observations.length} sources)</h4>
      <div
        style={{
          fontSize: '0.75rem',
          color: 'var(--text-secondary)',
          marginBottom: '0.5rem',
        }}
      >
        Source diversity. Multiple polls from the same source count as 1 entry
        (REPLACE-not-APPEND semantics).
      </div>
      {observations.map((obs, i) => (
        <details
          key={`${obs.source_id}-${i}`}
          style={{
            border: '1px solid var(--border-default, #e5e7eb)',
            borderRadius: '0.25rem',
            padding: '0.5rem',
            marginBottom: '0.5rem',
          }}
        >
          <summary
            style={{
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <strong style={{ fontFamily: 'monospace' }}>{obs.source_id}</strong>
            <span style={{ fontSize: '0.75rem' }}>
              {obs.observed_at
                ? new Date(obs.observed_at).toLocaleString()
                : 'unknown observed_at'}
            </span>
          </summary>
          <dl style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
            {obs.status && (
              <>
                <dt style={{ display: 'inline', fontWeight: 600 }}>Status:</dt>{' '}
                <dd style={{ display: 'inline', margin: 0 }}>{obs.status}</dd>
                <br />
              </>
            )}
            {obs.severity != null && (
              <>
                <dt style={{ display: 'inline', fontWeight: 600 }}>Severity:</dt>{' '}
                <dd style={{ display: 'inline', margin: 0 }}>
                  {obs.severity}
                  {obs.severity_label ? ` (${obs.severity_label})` : ''}
                </dd>
                <br />
              </>
            )}
            {obs.event_type && (
              <>
                <dt style={{ display: 'inline', fontWeight: 600 }}>Event type:</dt>{' '}
                <dd style={{ display: 'inline', margin: 0 }}>{obs.event_type}</dd>
                <br />
              </>
            )}
            {obs.backfilled && (
              <em style={{ fontSize: '0.75rem' }}>
                (Backfilled — first observed in pre-WQ-278 snapshot)
              </em>
            )}
          </dl>
          {/* WQ-286 Item 5: per-source description rendered when WQ-285
              parser-extracted description is non-empty. Pre-WQ-285 events
              (not re-correlated) carry empty descriptions and skip this
              block; full-empty fallback to NVD external-link is handled at
              the panel level. Whitespace preserved via pre-wrap per the
              `shared.normalize_description()` truncation contract. */}
          {typeof obs.description === 'string' && obs.description.length > 0 && (
            <div
              style={{
                marginTop: '0.5rem',
                paddingTop: '0.5rem',
                borderTop: '1px dashed var(--border-default, #e5e7eb)',
                fontSize: '0.8125rem',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                Description (from {obs.source_id})
              </div>
              <p
                style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  color: 'var(--text-secondary)',
                }}
              >
                {obs.description}
              </p>
            </div>
          )}
        </details>
      ))}
    </div>
  );
}

export function ThreatDetailPanel(): React.JSX.Element | null {
  const [searchParams, setSearchParams] = useSearchParams();
  const canonicalEventId = searchParams.get('canonical_event_id');
  const { data, isLoading, error, refetch } = useCanonicalEventDetail(canonicalEventId);
  const { authFetch } = useAuth();
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const [showSnooze, setShowSnooze] = useState(false);
  const [showDismissFp, setShowDismissFp] = useState(false);
  const [dismissRationale, setDismissRationale] = useState('');
  const [showReopen, setShowReopen] = useState(false);
  const [reopenRationale, setReopenRationale] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (canonicalEventId) panelRef.current?.focus();
  }, [canonicalEventId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && canonicalEventId) closePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalEventId]);

  if (!canonicalEventId) return null;

  const closePanel = () => {
    setSearchParams((prev) => {
      prev.delete('canonical_event_id');
      return prev;
    });
    setActionError(null);
    setActionStatus(null);
    setShowSnooze(false);
    setShowDismissFp(false);
    setDismissRationale('');
    setShowReopen(false);
    setReopenRationale('');
  };

  const sendToOrchestrator = async () => {
    if (!data) return;
    setActionError(null);
    setActionStatus('Submitting to orchestrator…');
    try {
      const resp = await securityApi.sendToOrchestrator(authFetch, data.id, {
        prompt_text: `Review ${data.cve_id} (${data.affected_target ?? 'platform-agnostic'}, severity ${data.max_severity ?? 'N/A'}, status ${data.latest_status}). Propose remediation.`,
      });
      setActionStatus(`Enqueued (id ${resp.queue_row_id.slice(0, 8)})`);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      setActionStatus(null);
    }
  };

  const sendToIssues = async () => {
    if (!data) return;
    setActionError(null);
    setActionStatus('Submitting to issues backlog…');
    try {
      const resp = await securityApi.sendToIssues(authFetch, data.id, {
        title: `[cyber-agent] ${data.cve_id}${data.affected_target ? ' — ' + data.affected_target : ''} (severity ${data.max_severity ?? 'N/A'})`,
        body_markdown: `## Summary\n- CVE: ${data.cve_id}\n- Affected: ${data.affected_target ?? '— (platform-agnostic)'}\n- Severity: ${data.max_severity ?? 'N/A'}\n- Status: ${data.latest_status}\n- Source count: ${data.source_observations.length}\n`,
        labels: ['security', 'cve', 'cyber-agent'],
      });
      setActionStatus(`Enqueued (id ${resp.queue_row_id.slice(0, 8)})`);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      setActionStatus(null);
    }
  };

  const acknowledgeEvent = async () => {
    if (!data) return;
    setActionError(null);
    setActionStatus('Acknowledging…');
    setActionBusy(true);
    try {
      await securityApi.acknowledgeEvent(authFetch, data.id, {});
      setActionStatus('Acknowledged');
      refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      setActionStatus(null);
    } finally {
      setActionBusy(false);
    }
  };

  const dismissAsFp = async () => {
    if (!data) return;
    if (dismissRationale.trim().length < 10) {
      setActionError('Rationale must be at least 10 characters');
      return;
    }
    setActionError(null);
    setActionStatus('Dismissing…');
    setActionBusy(true);
    try {
      await securityApi.dismissEventFP(authFetch, data.id, {
        rationale: dismissRationale.trim(),
      });
      setActionStatus('Dismissed as false positive');
      setShowDismissFp(false);
      setDismissRationale('');
      refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      setActionStatus(null);
    } finally {
      setActionBusy(false);
    }
  };

  // WQ-286 Item 1: re-open from dismissed_fp. Mirrors dismiss-fp UX pattern:
  // ≥10 char rationale required (terminal-state unfork is consequential).
  const reopenEvent = async () => {
    if (!data) return;
    if (reopenRationale.trim().length < 10) {
      setActionError('Rationale must be at least 10 characters');
      return;
    }
    setActionError(null);
    setActionStatus('Re-opening…');
    setActionBusy(true);
    try {
      await securityApi.reopenEvent(authFetch, data.id, {
        rationale: reopenRationale.trim(),
      });
      setActionStatus('Re-opened to active state');
      setShowReopen(false);
      setReopenRationale('');
      refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      setActionStatus(null);
    } finally {
      setActionBusy(false);
    }
  };

  const handleSnoozeConfirm = async (untilIso: string, rationale: string | undefined) => {
    if (!data) return;
    setActionError(null);
    setActionStatus('Snoozing…');
    setActionBusy(true);
    try {
      await securityApi.snoozeEvent(authFetch, data.id, {
        snoozed_until: untilIso,
        rationale,
      });
      setActionStatus('Snoozed');
      setShowSnooze(false);
      refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      setActionStatus(null);
    } finally {
      setActionBusy(false);
    }
  };

  const eligible = data ? isRoutingEligible({
    id: data.id,
    cve_id: data.cve_id,
    affected_target: data.affected_target,
    max_severity: data.max_severity,
    latest_status: data.latest_status,
    source_count: data.source_observations.length,
    first_seen_at: data.first_seen_at,
    last_seen_at: data.last_seen_at,
    operator_state: data.operator_state,
    snoozed_until: data.snoozed_until,
    state_changed_at: data.state_changed_at,
    state_changed_by_actor_id: data.state_changed_by_actor_id,
  }) : false;

  // Portal to document.body so the panel escapes `.admin-page-content`'s
  // stacking context (position: relative; z-index: 1). Without this, no
  // amount of z-index can stack the panel above `.admin-header` (z-index: 1000)
  // because the header sits in the PARENT stacking context. Pattern matches
  // NodeDetailModal / FinancialDetailModal / BulkImportModal in
  // components/adjacency-map/.
  const panelJsx = (
    <aside
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label={data ? `Threat detail for ${data.cve_id}` : 'Threat detail'}
      tabIndex={-1}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 'min(480px, 100vw)',
        background: 'var(--bg-primary, white)',
        borderLeft: '1px solid var(--border-default, #e5e7eb)',
        padding: '1rem',
        overflowY: 'auto',
        zIndex: 1001,
        boxShadow: '-4px 0 12px rgba(0,0,0,0.08)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.75rem',
        }}
      >
        <strong style={{ fontFamily: 'monospace', fontSize: '1rem' }}>
          {data?.cve_id ?? '…'}
        </strong>
        <button
          className="btn btn-ghost"
          onClick={closePanel}
          aria-label="Close detail panel"
        >
          ×
        </button>
      </div>

      {isLoading && !data && <LoadingSkeleton rows={4} />}
      {error && <ErrorState error={error} />}

      {data && (
        <>
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center',
              marginBottom: '0.75rem',
              flexWrap: 'wrap',
            }}
          >
            <SeverityBadge
              token={severityTokenFromScore(data.max_severity)}
              score={data.max_severity}
              showRoutingEligibilityChip={eligible}
            />
            <StatusBadge status={data.latest_status} />
            <OperatorStateBadge
              state={data.operator_state}
              snoozedUntil={data.snoozed_until}
            />
          </div>
          <dl style={{ marginBottom: '1rem' }}>
            <div>
              <dt style={{ display: 'inline', fontWeight: 600 }}>Affected:</dt>{' '}
              <dd style={{ display: 'inline', margin: 0 }}>
                {data.affected_target || '— (platform-agnostic)'}
              </dd>
            </div>
            <div>
              <dt style={{ display: 'inline', fontWeight: 600 }}>First seen:</dt>{' '}
              <dd style={{ display: 'inline', margin: 0 }}>
                {data.first_seen_at
                  ? new Date(data.first_seen_at).toLocaleString()
                  : '—'}
              </dd>
            </div>
            <div>
              <dt style={{ display: 'inline', fontWeight: 600 }}>Last seen:</dt>{' '}
              <dd style={{ display: 'inline', margin: 0 }}>
                {data.last_seen_at
                  ? new Date(data.last_seen_at).toLocaleString()
                  : '—'}
              </dd>
            </div>
          </dl>

          {/* WQ-286 Item 5: per-source descriptions render in each evidence
              card (see EvidenceTrail). Panel-level NVD external-link block
              only renders as fallback when ALL source_observations entries
              have empty `description` (pre-WQ-285 events not yet re-correlated). */}
          {!data.source_observations.some(
            (obs) => typeof obs.description === 'string' && obs.description.length > 0
          ) && (
            <div
              style={{
                marginBottom: '1rem',
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--border-default, #e5e7eb)',
                borderRadius: '0.25rem',
                background: 'var(--bg-secondary)',
                fontSize: '0.8125rem',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                Description
              </div>
              <div style={{ color: 'var(--text-secondary)' }}>
                Description text is not yet captured for this CVE
                (pre-WQ-285 event not re-correlated). Look up the canonical
                description at{' '}
                <a
                  href={`https://nvd.nist.gov/vuln/detail/${data.cve_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--severity-high-text, #b45309)' }}
                >
                  NVD: {data.cve_id} ↗
                </a>
                .
              </div>
            </div>
          )}

          <EvidenceTrail observations={data.source_observations} />

          <div
            style={{
              borderTop: '1px solid var(--border-default, #e5e7eb)',
              marginTop: '1rem',
              paddingTop: '1rem',
            }}
          >
            <h4 style={{ marginTop: 0 }}>Actions</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button
                className="btn btn-primary"
                onClick={sendToOrchestrator}
                disabled={!eligible}
                aria-disabled={!eligible}
                title={!eligible
                  ? 'Charter-floor rule: only severity high/critical or KEV-Exploited events are routing-eligible.'
                  : 'Enqueue this CVE as a remediation prompt for the orchestrator agent. Sends to platform_orchestrator_prompt_queue.'}
              >
                Send to orchestrator
              </button>
              <button
                className="btn btn-primary"
                onClick={sendToIssues}
                disabled={!eligible}
                aria-disabled={!eligible}
                title={!eligible
                  ? 'Charter-floor rule: only severity high/critical or KEV-Exploited events are routing-eligible.'
                  : 'Enqueue this CVE as a tracked issue for the open-issues backlog. Sends to platform_open_issues_queue.'}
              >
                Send to issues
              </button>
              <button
                className="btn btn-secondary"
                onClick={acknowledgeEvent}
                disabled={actionBusy || data.operator_state === 'dismissed_fp'}
                aria-disabled={actionBusy || data.operator_state === 'dismissed_fp'}
                title={data.operator_state === 'dismissed_fp'
                  ? 'Cannot acknowledge a dismissed-FP event (terminal state).'
                  : 'Mark as reviewed without taking remediation action yet. Clears any active snooze. Audit-logged.'}
              >
                Acknowledge
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowSnooze((s) => !s)}
                aria-expanded={showSnooze}
                disabled={actionBusy || data.operator_state === 'dismissed_fp'}
                aria-disabled={actionBusy || data.operator_state === 'dismissed_fp'}
                title={data.operator_state === 'dismissed_fp'
                  ? 'Cannot snooze a dismissed-FP event (terminal state).'
                  : 'Hide this CVE from the active threats view until a chosen future time. Pick a preset (24h/7d/30d) or custom date.'}
              >
                Snooze…
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowDismissFp((s) => !s)}
                aria-expanded={showDismissFp}
                disabled={actionBusy || data.operator_state === 'dismissed_fp'}
                aria-disabled={actionBusy || data.operator_state === 'dismissed_fp'}
                title={data.operator_state === 'dismissed_fp'
                  ? 'Already dismissed as false positive.'
                  : 'Mark as a false positive (FP) — this CVE does not actually apply here. Requires a written rationale (≥10 chars). Terminal state in v1; cannot be reversed.'}
              >
                Dismiss as FP
              </button>
              {/* WQ-286 Item 1: Re-open visible only when current state is
                  dismissed_fp. Terminal-state unfork; rationale ≥10 chars
                  required (matches dismiss-fp consequence floor). */}
              {data.operator_state === 'dismissed_fp' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowReopen((s) => !s)}
                  aria-expanded={showReopen}
                  disabled={actionBusy}
                  aria-disabled={actionBusy}
                  title="Re-open this dismissed-FP event back to active triage state. Use when new evidence shows the dismissal was incorrect. Requires a written rationale (≥10 chars)."
                >
                  Re-open
                </button>
              )}
            </div>
            {showSnooze && (
              <div style={{ marginTop: '0.75rem' }}>
                <SnoozePicker
                  onConfirm={handleSnoozeConfirm}
                  onCancel={() => setShowSnooze(false)}
                  busy={actionBusy}
                />
              </div>
            )}
            {showDismissFp && (
              <div
                style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  border: '1px solid var(--border-default, #e5e7eb)',
                  borderRadius: '0.25rem',
                }}
              >
                <label
                  style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}
                >
                  <strong>Rationale (required, ≥10 chars):</strong>
                  <textarea
                    value={dismissRationale}
                    onChange={(e) => setDismissRationale(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder="Explain why this is a false positive (required for audit trail)"
                  />
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowDismissFp(false);
                      setDismissRationale('');
                    }}
                    disabled={actionBusy}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={dismissAsFp}
                    disabled={actionBusy || dismissRationale.trim().length < 10}
                    aria-disabled={actionBusy || dismissRationale.trim().length < 10}
                  >
                    {actionBusy ? 'Dismissing…' : 'Dismiss as false positive'}
                  </button>
                </div>
              </div>
            )}
            {/* WQ-286 Item 1: Re-open form mirrors dismiss-fp UX (rationale ≥10 chars). */}
            {showReopen && (
              <div
                style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  border: '1px solid var(--border-default, #e5e7eb)',
                  borderRadius: '0.25rem',
                }}
              >
                <label
                  style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}
                >
                  <strong>Rationale (required, ≥10 chars):</strong>
                  <textarea
                    value={reopenRationale}
                    onChange={(e) => setReopenRationale(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder="Explain why re-opening this dismissed-FP event (e.g., new evidence shows the dismissal was incorrect)"
                  />
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowReopen(false);
                      setReopenRationale('');
                    }}
                    disabled={actionBusy}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={reopenEvent}
                    disabled={actionBusy || reopenRationale.trim().length < 10}
                    aria-disabled={actionBusy || reopenRationale.trim().length < 10}
                  >
                    {actionBusy ? 'Re-opening…' : 'Re-open to active'}
                  </button>
                </div>
              </div>
            )}
            {actionStatus && (
              <div
                role="status"
                aria-live="polite"
                style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}
              >
                {actionStatus}
              </div>
            )}
            {actionError && (
              <div
                role="alert"
                style={{
                  marginTop: '0.75rem',
                  color: 'var(--severity-critical-text, #991b1b)',
                }}
              >
                {actionError}
              </div>
            )}
          </div>
        </>
      )}
    </aside>
  );

  return createPortal(panelJsx, document.body);
}
